/**
 * Dealer Showroom building type configuration.
 * Automotive dealership environments: showroom, customer lounge, service areas.
 * Updated for Ford Signature 2.0 alignment with senSEI mapping.
 */

// Points system (DSHPS)
import {
  DS_PATHWAY_CAPS, DS_PRECONDITIONS, DS_FEATURES, DS_FEATURE_MAP,
  checkDsPreconditions, autoDetectDsFeatures, scoreDsSpace, scoreDsProject, getDsCertificationTier,
} from '../engine/bhpsDealer';

// Financial engines
import {
  DS_PATHWAY_COEFFICIENTS, DS_ROLE_DEFAULTS, DS_STAFF_EVIDENCE,
  DS_CUSTOMER_TIERS, DS_CUSTOMER_EVIDENCE, DS_CSI_TIERS, DS_FI_PER_UNIT,
  calculateDealerStaffRoi,
  calculateDealerCustomerRoi,
  calculateCsiBonus,
  calculateCsiLift,
} from '../engine/financialDealer';

export default {
  type: 'dealerShowroom',
  label: 'Dealer Showroom',
  shortLabel: 'Dealer',
  color: '#F59E0B',  // Amber
  icon: 'car',

  // --- Unit of analysis ---
  unitLabel: 'Space',
  unitLabelPlural: 'Spaces',
  unitIdField: 'spaceId',

  // --- Space types (includes Signature 2.0 vocabulary) ---
  spaceTypes: [
    { id: 'showroom_floor', label: 'Showroom Floor', icon: 'car' },
    { id: 'customer_lounge', label: 'Customer Lounge', icon: 'couch' },
    { id: 'service_waiting', label: 'Service Waiting Area', icon: 'clock' },
    { id: 'sales_office', label: 'Sales Office', icon: 'briefcase' },
    { id: 'delivery_bay', label: 'Delivery Bay', icon: 'truck' },
    { id: 'parts_counter', label: 'Parts Counter', icon: 'cogs' },
    { id: 'corridor', label: 'Corridor', icon: 'arrows-alt-h' },
    { id: 'general', label: 'General', icon: 'building' },
    // Signature 2.0 space types
    { id: 'hub', label: 'Hub / Welcome', icon: 'concierge-bell' },
    { id: 'consultation', label: 'Consultation Room', icon: 'comments' },
    { id: 'lifestyle_display', label: 'Lifestyle Display', icon: 'palette' },
    { id: 'exterior_arrival', label: 'Exterior / Arrival', icon: 'door-open' },
  ],

  // --- Input fields (standard 8-pathway building-scale) ---
  inputFields: {
    daylight_lux: { label: 'Daylight (lux)', unit: 'lux', pathway: 'daylight' },
    noise_db: { label: 'Noise Level (dB)', unit: 'dB', pathway: 'noise' },
    pm25_ug_m3: { label: 'PM2.5 (ug/m3)', unit: 'ug/m3', pathway: 'air_quality' },
    visual_pattern_stress: { label: 'Visual Pattern Stress (0-1)', unit: '', pathway: 'visual_stress' },
    enrichment_score: { label: 'Enrichment Score (0-1)', unit: '', pathway: 'enrichment' },
    wayfinding_score: { label: 'Wayfinding Score (0-1)', unit: '', pathway: 'wayfinding' },
    movement_minutes_day: { label: 'Movement (min/day)', unit: 'min', pathway: 'movement' },
    nature_minutes_day: { label: 'Nature Exposure (min/day)', unit: 'min', pathway: 'nature' },
    nature_view: { label: 'Nature View', unit: 'boolean', pathway: 'nature' },
  },

  // --- Points system (DSHPS) ---
  points: {
    name: 'Brain Health',
    fullName: 'Dealer Showroom Brain Health Points',
    pathwayCaps: DS_PATHWAY_CAPS,
    preconditions: DS_PRECONDITIONS,
    features: DS_FEATURES,
    featureMap: DS_FEATURE_MAP,
    tiers: { platinum: 75, gold: 60, silver: 45, bronze: 30 },
    checkPreconditions: checkDsPreconditions,
    autoDetectFeatures: autoDetectDsFeatures,
    scoreSpace: scoreDsSpace,
    scoreProject: scoreDsProject,
    getCertificationTier: getDsCertificationTier,
  },

  // --- Financial model ---
  financial: {
    staffLabel: 'Dealership Staff',
    occupantLabel: 'Customer',
    staffPathwayCoefficients: DS_PATHWAY_COEFFICIENTS,
    staffRoleDefaults: DS_ROLE_DEFAULTS,
    staffEvidence: DS_STAFF_EVIDENCE,
    calculateStaffRoi: calculateDealerStaffRoi,
    occupantAcuityTiers: DS_CUSTOMER_TIERS,
    occupantEvidence: DS_CUSTOMER_EVIDENCE,
    calculateOccupantRoi: calculateDealerCustomerRoi,
    calculateCsiBonus,
    calculateCsiLift,
    csiTiers: DS_CSI_TIERS,
    fiPerUnit: DS_FI_PER_UNIT,
    staffChannels: [
      { name: 'Sales Performance', key: 'sales_performance' },
      { name: 'Productivity', key: 'productivity' },
      { name: 'Retention', key: 'retention' },
    ],
    occupantChannels: [
      { name: 'Dwell Time → Conversion', key: 'conversion' },
      { name: 'F&I Revenue Lift', key: 'fi_revenue' },
      { name: 'Customer Satisfaction', key: 'satisfaction' },
      { name: 'Repeat Business', key: 'repeat_business' },
      { name: 'CSI Manufacturer Bonuses', key: 'csi_bonus' },
    ],
  },

  // --- Ford Signature 2.0 Pillar Mapping ---
  signature20: {
    pillars: [
      {
        id: 'hospitality_first',
        label: 'Hospitality First',
        color: '#2563EB',
        description: 'Staff as brand ambassadors, hospitality spaces, food & beverage',
        pathways: ['enrichment', 'noise', 'nature', 'air_quality'],
        senSEI: ['Customer Comfort', 'Sensory Experience'],
      },
      {
        id: 'discover_ford',
        label: 'Discover Ford',
        color: '#16A34A',
        description: 'Immersive brand experiences — see, touch, learn about products',
        pathways: ['enrichment', 'daylight', 'visual_stress'],
        senSEI: ['Interactive Learning', 'Dwell Time'],
      },
      {
        id: 'sales_service',
        label: 'Sales & Service Anywhere',
        color: '#EA580C',
        description: 'Flexible transaction spaces — traditional or hospitality',
        pathways: ['wayfinding', 'movement', 'noise'],
        senSEI: ['Purchase Points & Pathways', 'Relational Trust'],
      },
      {
        id: 'operations_excellence',
        label: 'Operations Excellence',
        color: '#6B7280',
        description: 'Efficient flow, optimized functionality, employee spaces',
        pathways: ['movement', 'air_quality', 'wayfinding'],
        senSEI: ['Employee Effectiveness'],
      },
    ],
    senSEI: {
      'Customer Comfort': {
        pathways: ['noise', 'air_quality', 'nature'],
        pillar: 'hospitality_first',
        description: 'Physical comfort during dealership visit — temperature, noise, air quality, seating',
        csiWeight: 0.25,
      },
      'Sensory Experience': {
        pathways: ['enrichment', 'daylight', 'nature'],
        pillar: 'hospitality_first',
        description: 'Multi-sensory engagement — visual variety, lighting quality, natural elements',
        csiWeight: 0.15,
      },
      'Interactive Learning': {
        pathways: ['enrichment', 'daylight'],
        pillar: 'discover_ford',
        description: 'Hands-on product exploration — configurators, displays, test drive staging',
        csiWeight: 0.15,
      },
      'Dwell Time': {
        pathways: ['enrichment', 'visual_stress', 'nature'],
        pillar: 'discover_ford',
        description: 'Time spent voluntarily exploring — correlated with conversion rate',
        csiWeight: 0.10,
      },
      'Purchase Points & Pathways': {
        pathways: ['wayfinding', 'movement'],
        pillar: 'sales_service',
        description: 'Clarity and flow of the customer purchase journey',
        csiWeight: 0.15,
      },
      'Relational Trust': {
        pathways: ['noise', 'enrichment', 'wayfinding'],
        pillar: 'sales_service',
        description: 'Environment supports trust-building — private consultation, comfortable negotiation',
        csiWeight: 0.10,
      },
      'Employee Effectiveness': {
        pathways: ['movement', 'air_quality', 'wayfinding', 'daylight'],
        pillar: 'operations_excellence',
        description: 'Staff productivity and wellbeing — workspace quality, circulation efficiency',
        csiWeight: 0.10,
      },
    },
  },

  // --- Optimizer targets ---
  optimizerTargets: {
    daylight: {
      field: 'daylight_lux', target: 500, unit: 'lux',
      rationale: 'Vehicle display and customer comfort require higher lux (Heschong 1999)',
      action: 'Increase showroom lighting',
      details: 'Maximize glazing, add skylights, use high-CRI LED for vehicle display',
      mechanism: 'Daylight activates ipRGC photoreceptors, driving circadian CREB signaling (Brown 2022)',
      category: 'moderate',
    },
    noise: {
      field: 'noise_db', target: 55, unit: 'dB',
      rationale: 'Conversation comfort for sales negotiations (Banbury & Berry 2005)',
      action: 'Reduce ambient noise levels',
      details: 'Acoustic ceiling, sound isolation from service bay, soft flooring in customer areas',
      mechanism: 'Noise above 80 dB inhibits LTP and reduces BDNF (de Deus 2017)',
      category: 'moderate',
    },
    air_quality: {
      field: 'pm25_ug_m3', target: 8, unit: 'ug/m3',
      rationale: 'Showroom air quality critical due to proximity to service operations (Allen 2015)',
      action: 'Improve air filtration and separation',
      details: 'Positive pressure showroom, MERV 13+ filtration, vehicle exhaust extraction',
      mechanism: 'PM2.5 causes neuroinflammation; impacts cognitive function (Liu 2021)',
      category: 'major_investment',
    },
    visual_stress: {
      field: 'visual_pattern_stress', target: 0.15, unit: 'score',
      rationale: 'Natural/varied patterns for customer comfort (Valentine 2025)',
      action: 'Reduce visual pattern stress',
      details: 'Replace uniform grids with varied geometry, natural materials, curved elements',
      mechanism: 'Repetitive patterns at ~3 cpd trigger cortical hyperactivation (Valentine 2025)',
      category: 'quick_win',
    },
    enrichment: {
      field: 'enrichment_score', target: 0.7, unit: '/10',
      rationale: 'Rich showroom environments increase dwell time and engagement (Spence 2014)',
      action: 'Increase environmental enrichment',
      details: 'Interactive displays, varied seating, brand storytelling, sensory variety',
      mechanism: 'Enrichment drives BDNF via cognitive challenge (r=0.62, Vega-Rosas 2024)',
      category: 'moderate',
    },
    wayfinding: {
      field: 'wayfinding_score', target: 0.8, unit: 'score',
      rationale: 'Customer journey clarity from entry through sales to delivery (Jamshidi 2025)',
      action: 'Improve customer journey wayfinding',
      details: 'Department signage, sightlines, color-coded zones, digital directories',
      mechanism: 'Good wayfinding engages hippocampal spatial navigation (Banner 2011)',
      category: 'quick_win',
    },
    nature: {
      fields: { nature_minutes_day: 15, nature_view: true, nature_quality: 0.5 },
      unit: 'combined',
      rationale: 'Nature elements reduce customer stress during high-stakes purchases (Hunter 2019)',
      action: 'Enhance nature access and views',
      details: 'Add indoor plants, living walls, outdoor waiting areas with landscaping',
      mechanism: 'Nature exposure reduces cortisol by 9.6%/hour (Hunter 2019)',
      category: 'moderate',
    },
    movement: {
      fields: { movement_minutes_day: 20, movement_intensity: 0.4 },
      unit: 'combined',
      rationale: 'Showroom layout that encourages browsing and walking (Conn 2009)',
      action: 'Design for customer circulation',
      details: 'Vehicle display layout encouraging movement, walking paths between departments',
      mechanism: 'Movement increases BDNF via PGC-1a/FNDC5/irisin pathway (Wrann 2013)',
      category: 'quick_win',
    },
  },

  // --- Neuroscience labels ---
  neuroscience: {
    outcomeLabels: {
      dorsal: 'Cognitive & Decision-Making (Dorsal Hippocampus)',
      ventral: 'Emotional & Comfort (Ventral Hippocampus)',
    },
    contextDescription: 'Showroom environments impact customer decision-making, comfort during high-stakes purchases, and staff sales performance through neurobiological pathways.',
    temporalLabels: {
      immediate: 'Immediate (minutes-hours)',
      shortTerm: 'Short-term (days-weeks)',
      longTerm: 'Long-term (months-years)',
    },
  },

  // --- Route prefix (uses unified assess routes) ---
  routePrefix: 'assess',
  projectTypeKey: 'dealer_showroom',

  // --- Demo data ---
  demos: [
    {
      id: 'boulevard_ford',
      label: 'Boulevard Ford — Delaware, OH',
      description: 'Ford Signature 2.0 pilot dealership. $12.5M investment.',
      loader: () => import('../demos/boulevardFord'),
    },
  ],

  // --- Feature flags ---
  hasOptimizer: true,
  hasPopulationEditor: true,
  hasSenSEI: true,
};
